// src/admin/DataMigration/tools/StudentProfileCsvTool.js
import React, { useState } from 'react';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';
import { firestore } from '../../../firebaseConfig';
import {
  normalizeStr,
  getStudentKey,
  cleanDate,
  cleanPhone,
  parseCSV
} from '../utils/migrationHelpers';

const StudentProfileCsvTool = ({ setProgress, setStatus, setTerminalLog, isProcessing }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreviewData, setCsvPreviewData] = useState(null);
  const [showCsvPreviewModal, setShowCsvPreviewModal] = useState(false);
  const [isParsingCsv, setIsParsingCsv] = useState(false);

  const handleCsvFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setCsvFile(e.target.files[0]);
    }
  };

  const handleScanCsvPreview = async () => {
    if (!csvFile) {
      alert('Vui lòng chọn file CSV trước khi quét.');
      return;
    }

    try {
      setIsParsingCsv(true);
      setTerminalLog(`🔍 Đang nạp danh sách thiếu nhi từ Firestore...\n`);

      const studentsSnap = await getDocs(collection(firestore, 'students'));
      const fsFullNameMap = {};
      const fsHolyAndFullNameMap = {};

      studentsSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        const normFull = normalizeStr(data.fullName);
        const key = getStudentKey(data.holyName, data.fullName);

        if (normFull) {
          if (!fsFullNameMap[normFull]) fsFullNameMap[normFull] = [];
          fsFullNameMap[normFull].push({ id: docSnap.id, ...data });
        }

        if (key && key !== '_') {
          if (!fsHolyAndFullNameMap[key]) fsHolyAndFullNameMap[key] = [];
          fsHolyAndFullNameMap[key].push({ id: docSnap.id, ...data });
        }
      });

      setTerminalLog(prev => prev + `✓ Đã nạp ${studentsSnap.size} thiếu nhi từ Firestore.\n📂 Đang đọc và phân tích file CSV: ${csvFile.name}...\n`);

      const fileText = await csvFile.text();
      const rows = parseCSV(fileText);

      if (rows.length === 0) {
        alert('File CSV rỗng hoặc không đúng định dạng.');
        setIsParsingCsv(false);
        return;
      }

      let headerIdx = -1;
      for (let i = 0; i < Math.min(20, rows.length); i++) {
        const lineStr = rows[i].join(' ').toUpperCase();
        if (lineStr.includes('HỌ TÊN') || lineStr.includes('HỌ VÀ TÊN') || lineStr.includes('SINH NGÀY')) {
          headerIdx = i;
          break;
        }
      }

      if (headerIdx === -1) {
        alert('Không tìm thấy dòng tiêu đề (Header) chứa HỌ TÊN hoặc SINH NGÀY trong file CSV.');
        setIsParsingCsv(false);
        return;
      }

      const headers = rows[headerIdx].map(h => h.toUpperCase().trim());

      const getColIdx = (keyword, startFrom = 0) => {
        for (let idx = startFrom; idx < headers.length; idx++) {
          if (headers[idx].includes(keyword)) return idx;
        }
        return -1;
      };

      const holyNameIdx = getColIdx('TÊN THÁNH');
      const hoTenIdx = getColIdx('HỌ TÊN') !== -1 ? getColIdx('HỌ TÊN') : getColIdx('HỌ VÀ TÊN');
      const tenIdx = headers.findIndex(h => h === 'TÊN');
      const dobIdx = getColIdx('SINH NGÀY');
      const pobIdx = getColIdx('NƠI SINH');
      const baptDateIdx = getColIdx('RỬA TỘI');
      const baptPlaceIdx = getColIdx('NƠI RỬA TỘI');
      const commIdx = getColIdx('RƯỚC LỄ');
      const confIdx = getColIdx('THÊM SỨC');

      const fatherNameIdx = getColIdx('TÊN CHA');
      const fatherBirthIdx = fatherNameIdx !== -1 ? getColIdx('NĂM SINH', fatherNameIdx) : -1;
      const fatherJobIdx = fatherNameIdx !== -1 ? getColIdx('NGHỀ NGHIỆP', fatherNameIdx) : -1;

      const motherNameIdx = getColIdx('TÊN MẸ');
      const motherBirthIdx = motherNameIdx !== -1 ? getColIdx('NĂM SINH', motherNameIdx) : -1;
      const motherJobIdx = motherNameIdx !== -1 ? getColIdx('NGHỀ NGHIỆP', motherNameIdx) : -1;

      const addressIdx = getColIdx('ĐỊA CHỈ');
      const subParishIdx = getColIdx('GIÁO KHU');
      const phone1Idx = getColIdx('ĐIỆN THOẠI 1') !== -1 ? getColIdx('ĐIỆN THOẠI 1') : getColIdx('ĐIỆN THOẠI');
      const phone2Idx = getColIdx('ĐIỆN THOẠI 2');

      const csvFullNameCounts = {};
      const csvKeyCounts = {};
      const dataRows = rows.slice(headerIdx + 1);

      dataRows.forEach(row => {
        let holyName = holyNameIdx !== -1 ? (row[holyNameIdx] || '') : '';
        let fullName = hoTenIdx !== -1 ? (row[hoTenIdx] || '') : '';
        if (tenIdx !== -1 && row[tenIdx] && !fullName.endsWith(row[tenIdx])) {
          fullName = `${fullName} ${row[tenIdx]}`.trim();
        }

        const normFull = normalizeStr(fullName);
        const key = getStudentKey(holyName, fullName);

        if (normFull) csvFullNameCounts[normFull] = (csvFullNameCounts[normFull] || 0) + 1;
        if (key && key !== '_') csvKeyCounts[key] = (csvKeyCounts[key] || 0) + 1;
      });

      const readyList = [];
      const skippedDuplicateList = [];
      const skippedNotFoundList = [];

      dataRows.forEach((row, idx) => {
        let holyName = holyNameIdx !== -1 ? (row[holyNameIdx] || '') : '';
        let fullName = hoTenIdx !== -1 ? (row[hoTenIdx] || '') : '';
        if (tenIdx !== -1 && row[tenIdx] && !fullName.endsWith(row[tenIdx])) {
          fullName = `${fullName} ${row[tenIdx]}`.trim();
        }

        const normFull = normalizeStr(fullName);
        const key = getStudentKey(holyName, fullName);
        if (!normFull) return;

        const studentPayload = {
          dateOfBirth: cleanDate(dobIdx !== -1 ? row[dobIdx] : ''),
          placeOfBirth: pobIdx !== -1 ? (row[pobIdx] || '') : '',
          baptismDate: cleanDate(baptDateIdx !== -1 ? row[baptDateIdx] : ''),
          baptismPlace: baptPlaceIdx !== -1 ? (row[baptPlaceIdx] || '') : '',
          firstCommunionDate: cleanDate(commIdx !== -1 ? row[commIdx] : ''),
          confirmationDate: cleanDate(confIdx !== -1 ? row[confIdx] : ''),
          fatherName: fatherNameIdx !== -1 ? (row[fatherNameIdx] || '') : '',
          fatherBirthYear: fatherBirthIdx !== -1 ? (row[fatherBirthIdx] || '') : '',
          fatherJob: fatherJobIdx !== -1 ? (row[fatherJobIdx] || '') : '',
          motherName: motherNameIdx !== -1 ? (row[motherNameIdx] || '') : '',
          motherBirthYear: motherBirthIdx !== -1 ? (row[motherBirthIdx] || '') : '',
          motherJob: motherJobIdx !== -1 ? (row[motherJobIdx] || '') : '',
          address: addressIdx !== -1 ? (row[addressIdx] || '') : '',
          subParish: subParishIdx !== -1 ? (row[subParishIdx] || '') : '',
          phone1: cleanPhone(phone1Idx !== -1 ? row[phone1Idx] : ''),
          phone2: cleanPhone(phone2Idx !== -1 ? row[phone2Idx] : '')
        };

        const displayItem = {
          csvRow: idx + headerIdx + 2,
          holyName: holyName,
          fullName: fullName,
          payload: studentPayload
        };

        const fsFullMatches = fsFullNameMap[normFull] || [];

        if (fsFullMatches.length === 0) {
          skippedNotFoundList.push({ ...displayItem, reason: 'Không tìm thấy Họ và Tên trong Firestore' });
        } else if (fsFullMatches.length === 1 && csvFullNameCounts[normFull] === 1) {
          const matched = fsFullMatches[0];
          readyList.push({
            ...displayItem,
            studentId: matched.id,
            firestoreHolyName: matched.holyName,
            firestoreFullName: matched.fullName,
            matchType: 'Khớp Họ Tên'
          });
        } else {
          const fsKeyMatches = fsHolyAndFullNameMap[key] || [];
          if (fsKeyMatches.length === 1 && csvKeyCounts[key] === 1) {
            const matched = fsKeyMatches[0];
            readyList.push({
              ...displayItem,
              studentId: matched.id,
              firestoreHolyName: matched.holyName,
              firestoreFullName: matched.fullName,
              matchType: 'Khớp Tên Thánh + Họ Tên'
            });
          } else if (fsKeyMatches.length === 0) {
            skippedNotFoundList.push({
              ...displayItem,
              reason: 'Trùng Họ Tên nhưng Tên Thánh không khớp em nào trên Firestore'
            });
          } else {
            skippedDuplicateList.push({
              ...displayItem,
              reason: 'Kết hợp cả Tên Thánh + Họ Tên vẫn bị trùng lặp (Bỏ qua để tránh nhầm lẫn)'
            });
          }
        }
      });

      setCsvPreviewData({
        readyList,
        skippedDuplicateList,
        skippedNotFoundList,
        stats: {
          totalCsvRows: dataRows.length,
          readyCount: readyList.length,
          duplicateCount: skippedDuplicateList.length,
          notFoundCount: skippedNotFoundList.length
        }
      });

      setTerminalLog(prev => prev + `✓ Phân tích hoàn tất! Sẵn sàng: ${readyList.length}, Trùng: ${skippedDuplicateList.length}, Không tìm thấy: ${skippedNotFoundList.length}\n`);
      setShowCsvPreviewModal(true);
    } catch (err) {
      console.error('Lỗi khi quét CSV:', err);
      alert('Lỗi phân tích CSV: ' + err.message);
    } finally {
      setIsParsingCsv(false);
    }
  };

  const handleConfirmCsvMigration = async () => {
    if (!csvPreviewData || csvPreviewData.readyList.length === 0) return;

    setShowCsvPreviewModal(false);
    setStatus('processing');
    setProgress(1);
    setTerminalLog(`🚀 Bắt đầu bổ sung sơ yếu lý lịch cho ${csvPreviewData.readyList.length} thiếu nhi...\n`);

    try {
      const readyList = csvPreviewData.readyList;
      const total = readyList.length;
      const CHUNK_SIZE = 50;
      let totalUpdated = 0;

      for (let i = 0; i < total; i += CHUNK_SIZE) {
        const chunk = readyList.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(firestore);

        chunk.forEach(item => {
          const studentRef = doc(firestore, 'students', item.studentId);
          const cleanPayload = {};
          Object.entries(item.payload).forEach(([k, v]) => {
            if (v !== '' && v !== null && v !== undefined) {
              cleanPayload[k] = v;
            }
          });
          cleanPayload.updatedAt = new Date().toISOString();
          batch.update(studentRef, cleanPayload);
        });

        await batch.commit();
        totalUpdated += chunk.length;
        const currentPct = Math.round((totalUpdated / total) * 100);

        setProgress(currentPct);
        setTerminalLog(prev => prev + `  + [Đã lưu lô]: ${totalUpdated}/${total} thiếu nhi (${currentPct}%)\n`);
      }

      setProgress(100);
      setStatus('done');
      setTerminalLog(prev => prev + `\n🎉 TỔNG KẾT: Đã bổ sung thành công sơ yếu lý lịch cho ${totalUpdated} thiếu nhi vào Firestore!\n`);
    } catch (err) {
      console.error('Lỗi khi cập nhật hồ sơ:', err);
      setStatus('error');
      setTerminalLog(prev => prev + `❌ LỖI LƯU HỒ SƠ: ${err.message}\n`);
    }
  };

  return (
    <div className="dmm-att-section-card">
      <div className="dmm-att-controls">
        <div className="dmm-select-group">
          <label>Chọn file CSV danh sách thiếu nhi (Sơ yếu lý lịch):</label>
          <input 
            type="file" 
            accept=".csv"
            onChange={handleCsvFileChange}
            className="dmm-file-input"
            disabled={isProcessing || isParsingCsv}
          />
        </div>

        <button
          onClick={handleScanCsvPreview}
          disabled={!csvFile || isProcessing || isParsingCsv}
          className="dmm-execute-btn"
        >
          {isParsingCsv ? '⏳ Đang quét CSV...' : '🔍 Phân Tích & Xem Trước'}
        </button>
      </div>

      {showCsvPreviewModal && csvPreviewData && (
        <div className="dmm-modal-backdrop">
          <div className="dmm-modal-card xlarge">
            <div className="dmm-modal-header">
              <h3>📋 BẢN XEM TRƯỚC BỔ SUNG SƠ YẾU LÝ LỊCH</h3>
              <button className="dmm-modal-close-btn" onClick={() => setShowCsvPreviewModal(false)}>✕</button>
            </div>

            <div className="dmm-modal-body">
              <div className="dmm-stats-grid">
                <div className="dmm-stat-card highlight">
                  <span className="stat-label">Hợp Lệ (Cập Nhật)</span>
                  <span className="stat-value">{csvPreviewData.stats.readyCount}</span>
                </div>
                <div className="dmm-stat-card warning">
                  <span className="stat-label">Trùng Lặp (Bỏ Qua)</span>
                  <span className="stat-value">{csvPreviewData.stats.duplicateCount}</span>
                </div>
                <div className="dmm-stat-card danger">
                  <span className="stat-label">Không Tìm Thấy (Bỏ Qua)</span>
                  <span className="stat-value">{csvPreviewData.stats.notFoundCount}</span>
                </div>
              </div>

              <h4 className="dmm-table-title">✅ Danh Sách Sẵn Sàng Cập Nhật ({csvPreviewData.readyList.length} em)</h4>
              <div className="dmm-preview-table-wrapper horizontal-scroll">
                <table className="dmm-preview-table full-columns">
                  <thead>
                    <tr>
                      <th>STT</th>
                      <th>Dòng CSV</th>
                      <th>Mã ID</th>
                      <th>Tên Thánh</th>
                      <th>Họ và Tên</th>
                      <th>SINH NGÀY</th>
                      <th>NƠI SINH</th>
                      <th>RỬA TỘI</th>
                      <th>NƠI RỬA TỘI</th>
                      <th>RƯỚC LỄ</th>
                      <th>THÊM SỨC</th>
                      <th>TÊN CHA</th>
                      <th>NĂM SINH CHA</th>
                      <th>NGHỀ NGHIỆP CHA</th>
                      <th>TÊN MẸ</th>
                      <th>NĂM SINH MẸ</th>
                      <th>NGHỀ NGHIỆP MẸ</th>
                      <th>ĐỊA CHỈ</th>
                      <th>GIÁO KHU</th>
                      <th>ĐIỆN THOẠI 1</th>
                      <th>ĐIỆN THOẠI 2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreviewData.readyList.map((item, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{item.csvRow}</td>
                        <td className="td-code">{item.studentId}</td>
                        <td>{item.firestoreHolyName || item.holyName || '-'}</td>
                        <td>
                          <strong>{item.firestoreFullName || item.fullName}</strong>
                          <div style={{ fontSize: '10px', color: '#16a34a' }}>({item.matchType})</div>
                        </td>
                        <td>{item.payload.dateOfBirth || '-'}</td>
                        <td>{item.payload.placeOfBirth || '-'}</td>
                        <td>{item.payload.baptismDate || '-'}</td>
                        <td>{item.payload.baptismPlace || '-'}</td>
                        <td>{item.payload.firstCommunionDate || '-'}</td>
                        <td>{item.payload.confirmationDate || '-'}</td>
                        <td>{item.payload.fatherName || '-'}</td>
                        <td>{item.payload.fatherBirthYear || '-'}</td>
                        <td>{item.payload.fatherJob || '-'}</td>
                        <td>{item.payload.motherName || '-'}</td>
                        <td>{item.payload.motherBirthYear || '-'}</td>
                        <td>{item.payload.motherJob || '-'}</td>
                        <td>{item.payload.address || '-'}</td>
                        <td>{item.payload.subParish || '-'}</td>
                        <td>{item.payload.phone1 || '-'}</td>
                        <td>{item.payload.phone2 || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="dmm-modal-footer">
              <button onClick={() => setShowCsvPreviewModal(false)} className="dmm-btn-cancel">✕ Hủy Bỏ</button>
              <button 
                onClick={handleConfirmCsvMigration}
                disabled={csvPreviewData.readyList.length === 0}
                className="dmm-btn-confirm"
              >
                🚀 Xác Nhận Lưu {csvPreviewData.readyList.length} Hồ Sơ Vào Firestore
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentProfileCsvTool;